<template>
    <div class="vx-max-w-screen-sm vx-flex vx-flex-col vx-gap-4">
        <page-header>Profile Details</page-header>

        <vx-expandable-card>
            <template v-slot:content>
                <div class="vx-flex vx-flex-col vx-gap-6">
                    <vx-alert v-if="success" :closeable="false" color="success" shadow> Updated successfully </vx-alert>
                    <vx-alert v-if="error" :closeable="false" color="danger" shadow>
                        {{ error }}
                    </vx-alert>

                    <div>
                        <vx-label size="md"> Name </vx-label>

                        <vx-input
                            size="lg"
                            name="name"
                            v-model="user.name"
                            :error="displayFirstError(validate.name) || displayFirstError(errors.name)"
                        ></vx-input>
                    </div>

                    <div>
                        <vx-label size="md"> Email </vx-label>

                        <vx-input
                            size="lg"
                            name="email"
                            v-model="user.email"
                            :error="displayFirstError(validate.email) || displayFirstError(errors.email)"
                        ></vx-input>
                    </div>

                    <div>
                        <vx-label size="md">Time-Zone</vx-label>

                        <vx-selectable
                            size="lg"
                            name="profile_time_zone"
                            v-model="user.time_zone"
                            data-test="profile-time-zone"
                            :clearable="false"
                            :error="displayFirstError(validate.time_zone) || displayFirstError(errors.time_zone)"
                            :options="timeZonesList"
                            :reduce="(option) => option.value"
                        >
                        </vx-selectable>
                    </div>

                    <vx-button
                        block
                        size="lg"
                        @click.prevent="submit"
                        :loading="submitting"
                        :disabled="!validate.$anyDirty"
                    >
                        Update
                    </vx-button>
                </div>
            </template>
        </vx-expandable-card>
    </div>
</template>

<script setup>
import PageHeader from '../../general/PageHeader.vue';
import { VxAlert, VxButton, VxExpandableCard, VxInput, VxLabel, VxSelectable } from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import { displayFirstError } from '~/utils/validation';
import timeZones from '~/utils/timeZones';
import meClient from '~/services/meClient';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_UNPROCESSABLE_ENTITY } from '~/support/HttpStatuses';
import useVuelidate from '@vuelidate/core';
import { email, maxLength, required } from '@vuelidate/validators';

const user = defineModel('user', {
    type: Object,
    required: true,
});

const timeZonesList = computed(() =>
    timeZones.map((timeZone) => ({
        value: timeZone,
        label: timeZone.split('_').join(' '),
    }))
);

const submitting = ref(false);
const success = ref(false);
const error = ref('');
const errors = ref({});

const validate = useVuelidate(
    {
        name: { required, maxLength: maxLength(191) },
        email: { required, email, maxLength: maxLength(191) },
        time_zone: { required, maxLength: maxLength(30) },
    },
    user,
    {
        $autoDirty: true,
    }
);

const submit = async () => {
    if (submitting.value) {
        return;
    }

    const isValid = await validate.value.$validate();

    if (!isValid) {
        error.value('Please check form data.');
        return;
    }

    success.value = false;
    errors.value = {};
    error.value = '';
    submitting.value = true;

    try {
        await meClient.update({
            name: user.value.name,
            email: user.value.email,
            time_zone: user.value.time_zone,
        });
        success.value = true;
        validate.value.$reset();
    } catch (e) {
        console.error(e);
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            error.value = 'Something went wrong.';
        } else {
            if (e.response.status === HTTP_UNPROCESSABLE_ENTITY) {
                errors.value = e.response.data.errors;
            }
            error.value = e.response?.data?.message || 'Something went wrong.';
        }
    } finally {
        submitting.value = false;
    }
};
</script>
